import { equals } from 'ramda';

type Requester<T, P> = (params: P) => Promise<T>;
type WatcherCallback<T> = (data: T) => void;

type ChildWatchers<T> = {
  [key in keyof T]?: WatcherCallback<any>[];
};
interface RootWatchers<T> {
  _root_watcher: WatcherCallback<T>[];
}
type Watchers<T> = ChildWatchers<T> & RootWatchers<T>;
export class RemoteStorage<T, P> {
  private promise?: Promise<T>;
  private watchers: Watchers<T>;
  private requester: Requester<T, P>;
  constructor(fn: Requester<T, P>) {
    this.watchers = {
      _root_watcher: []
    };
    this.requester = fn;
  }
  private notify(res: any) {
    Object.keys(this.watchers).forEach(async v => {
      const key = v as keyof Watchers<T>;
      if (key === '_root_watcher') {
        this.watchers._root_watcher.forEach(cb => cb(res));
      } else {
        const watchers = this.watchers[key];
        if (watchers) {
          const oldVal = this.promise ? (await this.promise)[key] : null;
          const newVal = res[key];
          if (!oldVal || !equals(oldVal, newVal)) {
            watchers.forEach(cb => cb(res[key]));
          }
        }
      }
    });
  }
  async get(params: P) {
    if (!this.promise) this.promise = this.requester(params);
    return this.promise;
  }
  async refresh(params: P) {
    const fn = this.requester(params);
    const res = await fn;
    this.notify(res);
    this.promise = fn;
  }
  subscribe(fn: WatcherCallback<T>): void;
  subscribe<T1 = any>(namespace: keyof T, fn: WatcherCallback<T1>): void;
  subscribe<T1 = any>(
    arg1: WatcherCallback<T> | keyof T,
    arg2?: WatcherCallback<T1>
  ) {
    if (arg1 instanceof Function) {
      this.watchers._root_watcher.push(arg1);
    } else {
      if (!arg2) return;
      if (this.watchers[arg1]) {
        (this.watchers[arg1] as any).push(arg2);
      } else {
        (this.watchers[arg1] as any) = [arg2];
      }
    }
  }
  unsubscribe(fn: WatcherCallback<T>): void;
  unsubscribe<T1 = any>(namespace: keyof T, fn: WatcherCallback<T1>): void;
  unsubscribe<T1 = any>(
    arg1: WatcherCallback<T> | keyof T,
    arg2?: WatcherCallback<T1>
  ) {
    if (arg1 instanceof Function) {
      const index = this.watchers._root_watcher.findIndex(v => v === arg1);
      if (index < 0) return;
      this.watchers._root_watcher.splice(index, 0);
    } else {
      if (!arg2 || !this.watchers[arg1]) return;
      const index = (this.watchers[arg1] as any).findIndex(
        (v: any) => v === arg1
      );
      if (index < 0) return;
      (this.watchers[arg1] as any).splice(index, 0);
    }
  }
}
